import { useState } from 'react';
import { clsx } from 'clsx';
import { MessageCircle, Phone, Copy, Check, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '@/components/common/Toast';

export interface CardActionsProps {
  whatsappUrl?: string;
  phone?: string;
  message?: string;
  isProcessed: boolean;
  onToggleProcessed: () => void;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  hasExpandableContent?: boolean;
  className?: string;
}

export function CardActions({
  whatsappUrl,
  phone,
  message,
  isProcessed,
  onToggleProcessed,
  expanded = false,
  onToggleExpanded,
  hasExpandableContent = true,
  className,
}: CardActionsProps) {
  const [copiedType, setCopiedType] = useState<'phone' | 'message' | null>(null);
  const { showToast } = useToast();

  const handleCopy = async (text: string, type: 'phone' | 'message', label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedType(type);
      showToast(`${label} copiado al portapapeles`, 'success');
      setTimeout(() => setCopiedType(null), 2000);
    } catch {
      showToast('No se pudo copiar al portapapeles', 'error');
    }
  };

  return (
    <div className={clsx('flex items-center gap-2 flex-wrap', className)}>
      {whatsappUrl && (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
          title="Abrir chat en WhatsApp"
        >
          <MessageCircle size={14} />
          <span>WhatsApp</span>
        </a>
      )}

      {phone && (
        <button
          type="button"
          onClick={() => handleCopy(phone, 'phone', 'Teléfono')}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-surface-secondary text-text-secondary hover:bg-surface-tertiary rounded-lg text-xs font-medium transition-colors cursor-pointer"
          title="Copiar número de teléfono"
        >
          {copiedType === 'phone' ? (
            <Check size={13} className="text-brand-success" />
          ) : (
            <Phone size={13} />
          )}
          <span>{phone}</span>
        </button>
      )}

      {message && (
        <button
          type="button"
          onClick={() => handleCopy(message, 'message', 'Mensaje')}
          className="inline-flex items-center gap-1 px-2 py-1.5 bg-surface-secondary text-text-secondary hover:bg-surface-tertiary rounded-lg text-xs font-medium transition-colors cursor-pointer"
          title="Copiar texto del mensaje"
        >
          {copiedType === 'message' ? (
            <Check size={13} className="text-brand-success" />
          ) : (
            <Copy size={13} />
          )}
          <span className="hidden sm:inline">Mensaje</span>
        </button>
      )}

      <button
        type="button"
        onClick={onToggleProcessed}
        className={clsx(
          'ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer',
          isProcessed
            ? 'bg-brand-success/15 text-brand-success font-semibold'
            : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary'
        )}
        title={isProcessed ? 'Marcar como no procesado' : 'Marcar como procesado'}
      >
        <CheckCircle2 size={14} />
        <span>{isProcessed ? 'Procesado' : 'Marcar'}</span>
      </button>

      {hasExpandableContent && onToggleExpanded && (
        <button
          type="button"
          onClick={onToggleExpanded}
          className="p-1.5 rounded-lg text-text-muted hover:bg-surface-secondary transition-colors cursor-pointer"
          title={expanded ? 'Ocultar detalles' : 'Ver detalles'}
        >
          {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      )}
    </div>
  );
}
